import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  ChartBar, TrendUp, Warning, Clock, ArrowsClockwise,
  Users, FileText, ShieldCheck, CheckCircle, CurrencyEur,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const FUNNEL_COLORS = [
  'from-slate-400 to-slate-500',
  'from-blue-400 to-blue-500',
  'from-purple-400 to-purple-500',
  'from-amber-400 to-amber-500',
  'from-cyan-400 to-cyan-500',
  'from-teal-400 to-teal-500',
  'from-green-500 to-green-600',
];

export default function FunnelDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/dashboard/funnel`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
      else toast.error('Errore caricamento');
    } catch (e) { toast.error('Errore'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="p-8 text-slate-500">Caricamento...</div>;
  if (!data) return null;

  const maxFunnel = Math.max(...data.funnel.map(f => f.count), 1);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="funnel-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <ChartBar size={32} weight="duotone" className="text-orange-500" />
            Funnel Commerciale
          </h1>
          <p className="text-slate-500 mt-1">Pipeline vendite, conversioni e tempi medi.</p>
        </div>
        <Button variant="outline" onClick={fetchData}><ArrowsClockwise size={16} className="mr-2" />Aggiorna</Button>
      </div>

      {/* TREND 30D */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card icon={Users} label="Nuovi lead 30gg" value={data.trend_30d.new_leads} color="text-blue-600 bg-blue-50" />
        <Card icon={FileText} label="Contratti aperti 30gg" value={data.trend_30d.new_contracts} color="text-purple-600 bg-purple-50" />
        <Card icon={CheckCircle} label="Attivati 30gg" value={data.trend_30d.activated} color="text-green-600 bg-green-50" />
      </div>

      {/* FUNNEL VISUAL */}
      <div className="bg-white border rounded-lg p-6 mb-6">
        <h2 className="font-bold mb-4 flex items-center gap-2">
          <TrendUp size={20} className="text-orange-500" /> Pipeline conversione
        </h2>
        <div className="space-y-2">
          {data.funnel.map((f, i) => {
            const widthPct = (f.count / maxFunnel) * 100;
            return (
              <div key={f.key} className="flex items-center gap-3" data-testid={`funnel-${f.key}`}>
                <div className="w-32 text-sm font-medium text-slate-700">{f.stage}</div>
                <div className="flex-1 relative h-10 bg-slate-50 rounded">
                  <div className={`h-full rounded bg-gradient-to-r ${FUNNEL_COLORS[i]} transition-all flex items-center px-3`}
                       style={{ width: `${Math.max(widthPct, 8)}%` }}>
                    <span className="font-bold text-white text-sm">{f.count}</span>
                  </div>
                </div>
                <div className="w-24 text-right text-xs text-slate-500">
                  {i > 0 && <span className="text-slate-700 font-medium">{f.conversion_pct}%</span>}
                  {i > 0 && <span className="text-slate-400"> step</span>}
                </div>
                <div className="w-20 text-right text-xs">
                  <span className="text-slate-700 font-medium">{f.overall_pct}%</span>
                  <span className="text-slate-400"> tot</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* TIMING */}
        <div className="bg-white border rounded-lg p-6">
          <h2 className="font-bold mb-4 flex items-center gap-2">
            <Clock size={20} className="text-amber-500" /> Tempi medi (giorni)
          </h2>
          <div className="space-y-3">
            <Timing label="Lead → Firmato" value={data.timing.avg_days_lead_to_signed} />
            <Timing label="Firmato → Doc Check" value={data.timing.avg_days_signed_to_doc_check} />
            <Timing label="Doc Check → Credit Check" value={data.timing.avg_days_doc_to_credit} />
            <Timing label="Credit Check → Attivazione" value={data.timing.avg_days_credit_to_active} />
          </div>
        </div>

        {/* STUCK */}
        <div className="bg-white border rounded-lg p-6">
          <h2 className="font-bold mb-4 flex items-center gap-2">
            <Warning size={20} className="text-red-500" />
            Contratti fermi (&gt;3gg)
            {data.stuck.length > 0 && <Badge variant="outline" className="ml-2">{data.stuck.length}</Badge>}
          </h2>
          {data.stuck.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Nessun collo di bottiglia 🎉</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {data.stuck.map(s => (
                <div key={s.id} onClick={() => navigate(`/contracts`)}
                     className="flex justify-between items-center text-sm border-b pb-2 cursor-pointer hover:bg-slate-50 px-2 rounded"
                     data-testid={`stuck-${s.id}`}>
                  <div>
                    <div className="font-medium">{s.ragione_sociale}</div>
                    <Badge variant="outline" className="text-xs mt-1">{s.status}</Badge>
                  </div>
                  <div className="text-red-600 font-bold">{s.days_stuck}gg</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border rounded-lg p-4 flex items-center gap-3">
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
        <Icon size={24} weight="duotone" />
      </div>
      <div>
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-2xl font-bold">{value}</div>
      </div>
    </div>
  );
}

function Timing({ label, value }) {
  return (
    <div className="flex justify-between items-center border-b pb-2">
      <span className="text-sm text-slate-700">{label}</span>
      <span className="font-bold text-slate-900">
        {value === null || value === undefined ? '—' : `${value} gg`}
      </span>
    </div>
  );
}
